import axios from 'axios'
import { API_BASE_URL } from '../config/api'
import { refreshToken } from '../api'

let subscribers = []
let isAlreadyFetchingAccessToken = false

export const instance = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

export const instanceImage = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'multipart/form-data' },
})

function onAccessTokenFetched(token) {
  subscribers.forEach(callback => callback(token))
  subscribers = []
}

function addSubscriber(callback) {
  subscribers.push(callback)
}

export const logout = () => {
  
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('token')
  window.location.href = '/login'
}

export function configureAxiosDefaults(header, value) {
  instance.defaults.headers.common[header] = value
  instanceImage.defaults.headers.common[header] = value
}

export const interceptorToken = () => {
  instance.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => Promise.reject(error)
  );
  
  instance.interceptors.response.use(
    res => res.data || res,
    async e => {
      // Check if response exists before accessing properties
      if (!e.response) {
        return Promise.reject(e);
      }

      const originalRequest = e.response.config;
      
      if (e.response.status === 401) {
        // Check if the request is a login attempt
        const requestUrl = originalRequest.url;
        
        // Add all login-related endpoints to prevent logout redirection
        const loginRelatedEndpoints = [
          'auth/jwt/create/',
          '/api/token/', 
          'api/token/',
          '/api/token/refresh/',
          'api/token/refresh/'
        ];
        
        // Check if the current request is a login attempt
        const isLoginAttempt = loginRelatedEndpoints.some(endpoint => requestUrl.includes(endpoint));
        
        if (isLoginAttempt) {
          // This is a login-related request, don't redirect, just reject with the error
          return Promise.reject(e);
        }

        if (!isAlreadyFetchingAccessToken) {
          isAlreadyFetchingAccessToken = true;
          //localStorage.removeItem('token')
          refreshToken(localStorage.getItem('refreshToken'))
            .then(response => {
              const newToken = response.data.access;
              localStorage.setItem('token', newToken);
              configureAxiosDefaults('Authorization', newToken ? `Bearer ${newToken}` : null);
              isAlreadyFetchingAccessToken = false;
              onAccessTokenFetched(newToken);
            })
            // eslint-disable-next-line no-unused-vars
            .catch(error => {
              isAlreadyFetchingAccessToken = false; // Reset flag on error too
              logout();
            });
        }
        
        const retryOriginalRequest = new Promise(resolve => {
          addSubscriber(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          });
        });
        
        return retryOriginalRequest;
      }
      
      if (e.response.status === 500 || e.response.status === 422 || e.response.status === 400) {
        return Promise.reject(e);
      }
      
      if (e.response.status === 404) {
        return Promise.reject(e);
      }
      
      logout();
      return Promise.reject(e);
    }
  );
};

export function removeAxiosTokenHeader() {
  const header = 'Authorization'
  delete instance.defaults.headers.common[header]
}

export function post(url, data) {
  return instance
    .post(url, data)
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        // For standard API errors with response
        const res = { 
          code: error.response.status, 
          msg: error.response.data, 
          error 
        };
        throw res;
      } else if (error.message) {
        // For network errors or other errors without response
        const res = { 
          code: 500, 
          msg: error.message, 
          error 
        };
        throw res;
      } else {
        // Fallback for unexpected error formats
        const res = { 
          code: 500, 
          msg: "Unknown error occurred", 
          error 
        };
        throw res;
      }
    });
}

export function postImage(url, data) {
  return instanceImage
    .post(url, data)
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error.message, error }
        throw res
      }
    })
}

export function get(url, config) {
  return instance
    .get(url, config)
    .then(response => {
      if (response.data && response.status) {
        return response.data
      }
      return response
    })
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error?.message, error }
        throw res
      }
    })
}

export function put(url, data) {
  return instance
    .put(url, data)
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error.message, error }
        throw res
      }
    })
}

export function putImage(url, data) {
  return instanceImage
    .put(url, data)
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error.message, error }
        throw res
      }
    })
}

export function patch(url, data) {
  return instance
    .patch(url, data)
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error.message, error }
        throw res
      }
    })
}

export function remove(url, data) {
  return instance
    .delete(url, { data })
    .then(response => response)
    .catch(error => {
      if (error && error.response) {
        const res = { code: error?.response?.status, msg: error?.response?.data, error }
        throw res
      } else {
        const res = { code: 500, msg: error.message, error }
        throw res
      }
    })
}

/**
 * Image URL utilities for thumbnail support
 * Use these helpers to get optimized image URLs
 */

/**
 * Get the best available image URL with fallback
 * @param {object} obj - Object containing image fields (e.g., user, venue, event)
 * @param {string} thumbnailField - Name of thumbnail field (e.g., 'avatar_thumbnail', 'thumbnail')
 * @param {string} originalField - Name of original field (e.g., 'avatar', 'image')
 * @param {string} fallback - Fallback URL if no image exists
 * @returns {string} Full image URL
 */
export function getImageUrl(obj, thumbnailField, originalField, fallback = null) {
  if (!obj) return fallback
  
  const thumbnail = obj[thumbnailField]
  const original = obj[originalField]
  
  const imagePath = thumbnail || original
  
  if (!imagePath) return fallback
  
  // If already a full URL, return as-is
  if (imagePath.startsWith('http')) return imagePath
  
  return `${API_BASE_URL}${imagePath}`
}

/**
 * Get avatar URL with thumbnail preference
 * @param {object} user - User object with avatar and avatar_thumbnail fields
 * @param {string} fallback - Fallback image URL
 * @returns {string} Avatar URL (thumbnail if available, otherwise original)
 */
export function getAvatarUrl(user, fallback = null) {
  return getImageUrl(user, 'avatar_thumbnail', 'avatar', fallback)
}

/**
 * Get full-size avatar URL (for profile pages, modals)
 * @param {object} user - User object with avatar field
 * @param {string} fallback - Fallback image URL
 * @returns {string} Full avatar URL
 */
export function getAvatarFullUrl(user, fallback = null) {
  if (!user?.avatar) return fallback
  if (user.avatar.startsWith('http')) return user.avatar
  return `${API_BASE_URL}${user.avatar}`
}

/**
 * Get venue/event/group image URL with thumbnail preference
 * @param {object} obj - Object with image, thumbnail, and medium fields
 * @param {string} size - 'thumbnail', 'medium', or 'full'
 * @param {string} fallback - Fallback image URL
 * @returns {string} Image URL
 */
export function getEntityImageUrl(obj, size = 'thumbnail', fallback = null) {
  if (!obj) return fallback
  
  let imagePath
  switch (size) {
    case 'thumbnail':
      imagePath = obj.thumbnail || obj.image
      break
    case 'medium':
      imagePath = obj.medium || obj.thumbnail || obj.image
      break
    case 'full':
    default:
      imagePath = obj.image
      break
  }
  
  if (!imagePath) return fallback
  if (imagePath.startsWith('http')) return imagePath
  return `${API_BASE_URL}${imagePath}`
}

/**
 * Get background image URL with thumbnail preference
 * @param {object} obj - Object with background and background_thumbnail fields
 * @param {boolean} useThumbnail - Whether to prefer thumbnail
 * @param {string} fallback - Fallback image URL
 * @returns {string} Background URL
 */
export function getBackgroundUrl(obj, useThumbnail = true, fallback = null) {
  if (!obj) return fallback
  
  const imagePath = useThumbnail 
    ? (obj.background_thumbnail || obj.background)
    : obj.background
  
  if (!imagePath) return fallback
  if (imagePath.startsWith('http')) return imagePath
  return `${API_BASE_URL}${imagePath}`
}

/**
 * Get gallery item image URL with size preference
 * @param {object} item - Gallery item with image, thumbnail, medium fields
 * @param {string} size - 'thumbnail', 'medium', or 'full'
 * @param {string} fallback - Fallback image URL
 * @returns {string} Image URL
 */
export function getGalleryImageUrl(item, size = 'thumbnail', fallback = null) {
  return getEntityImageUrl(item, size, fallback)
}
